import { Router } from '@angular/router';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { ReservationFacilitySpecial } from '../../firestore-models/reservation-facility-special';
import { ProjectLanguage } from '../../firestore-models/project-language';
import { ApiReservationFacilitySpecial } from '../../api-client-module';
import { ApiSetReservationFacilitySpecialsRequest } from '../../api-client-module'

import { ApiClientService } from '../../api-client.service';
import { ConfigurationService } from '../../configuration.service';
import { SpinnerService } from '../../services/spinner.service';
import { AppConst } from '../../app-const';

import { ReservationFacilitySpecialDialogComponent } from './reservation-facility-special-dialog/reservation-facility-special-dialog.component';
import { MessageDialogComponent } from '../../common/message-dialog/message-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-reservation-facility-special',
  templateUrl: './reservation-facility-special.component.html',
  styleUrls: ['./reservation-facility-special.component.css'],
  standalone: false,
})
export class ReservationFacilitySpecialComponent implements OnInit, OnDestroy {

  //ReservationFacilityコレクション
  private reservationFacilitySpecialsCollection!: AngularFirestoreCollection<ReservationFacilitySpecial>;
  reservationFacilitySpecialsObservable!: Observable<ReservationFacilitySpecial[]>;
  reservationFacilitySpecialsSubscribe!: Subscription;
  //各予約設備情報に紐付くプロジェクトID
  projectId: string;
  //各予約設備情報に紐付くプロジェクトの言語情報
  projectLanguages!: Array<ProjectLanguage>;

  reservationFacilityId: string;

  reservationSpecialDateTypes = AppConst.RESERVATION_SPECIAL_DATE_TYPES;

  protected SEARCH_WORD_PREFIX: string = 'reservationSpecialDateType';

  //予約設備特別日情報テーブル
  displayedColumns: string[] = ['position', 'edit', 'select', 'type', 'start_date', 'end_date', 'date'];
  dataSource = new MatTableDataSource<ReservationFacilitySpecial>();
  selection = new SelectionModel<ReservationFacilitySpecial>(true, []);

  searchFilter: any = {};

  currentLang: string = 'ja';

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    private router: Router,
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
    this.projectId = this.configurationService.getProjectId();
    this.reservationFacilityId = this.configurationService.getReservationFacilityId();
  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.setCustomFilter();
    this.currentLang = this.translate.currentLang;
    // 特別営業日取得
    this.reservationFacilitySpecialsCollection = this.angularFirestore.collection<ReservationFacilitySpecial>(
      AppConst.FIRESTORE_RESERVATION_FACILITY_SPECIALS, ref => ref.where('project_id', '==', this.projectId).where('reservation_facility_id', '==', this.reservationFacilityId).orderBy('reservation_special_start_date', 'asc'));
    this.reservationFacilitySpecialsObservable = this.reservationFacilitySpecialsCollection.valueChanges({ idField: 'id' });
    this.reservationFacilitySpecialsSubscribe = this.reservationFacilitySpecialsObservable.subscribe(reservationFacilitySpecials => {
      this.dataSource.data = this.addFilterValue(reservationFacilitySpecials);
    });
  }

  ngOnDestroy(): void {
    this.reservationFacilitySpecialsSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: ReservationFacilitySpecial): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  // 編集ダイアログ
  openEditDialog(reservationFacilitySpecial: ReservationFacilitySpecial): void {
    const startDate: any = reservationFacilitySpecial!.reservation_special_start_date ? new Date(reservationFacilitySpecial!.reservation_special_start_date.seconds * 1000) : null;
    const endDate: any = reservationFacilitySpecial!.reservation_special_end_date ? new Date(reservationFacilitySpecial!.reservation_special_end_date.seconds * 1000) : null;
    let apiReservationFacilitySpecial: ApiReservationFacilitySpecial = {
      id: reservationFacilitySpecial.id,
      reservation_facility_special_id: reservationFacilitySpecial.reservation_facility_special_id,
      reservation_facility_id: reservationFacilitySpecial.reservation_facility_id,
      project_id: reservationFacilitySpecial.project_id,
      reservation_special_date_type: reservationFacilitySpecial.reservation_special_date_type,
      reservation_special_start_date: startDate,
      reservation_special_end_date: endDate,
      reservation_special_count: reservationFacilitySpecial.reservation_special_count,
      reservation_select_times: reservationFacilitySpecial.reservation_select_times,
      enabled: reservationFacilitySpecial.enabled,
    };
    const dialogRef = this.dialog.open(ReservationFacilitySpecialDialogComponent, {
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, reservationFacilitySpecial: apiReservationFacilitySpecial, reservationFacilityId: this.reservationFacilityId }
    });
  }

  //追加ダイアログ
  openAddDialog(): void {
    const dialogRef = this.dialog.open(ReservationFacilitySpecialDialogComponent, {
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, reservationFacilitySpecial: null }
    });
  }

  //削除
  deleteData(): void {
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('選択されたデータを削除します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedReservationFacilitySpecials: Array<ApiReservationFacilitySpecial> = new Array<ApiReservationFacilitySpecial>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const selectedReservationFacilitySpecial: ApiReservationFacilitySpecial = {
            id: row.id,
            reservation_facility_special_id: row.reservation_facility_special_id,
            reservation_facility_id: row.reservation_facility_id,
            project_id: row.project_id,
            reservation_special_date_type: row.reservation_special_date_type,
            reservation_select_times: row.reservation_select_times,
            enabled: row.enabled,
          };
          selectedReservationFacilitySpecials.push(selectedReservationFacilitySpecial);
        }
      }
      if (!!result) {
        let request: ApiSetReservationFacilitySpecialsRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          reservation_facility_specials: selectedReservationFacilitySpecials
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setReservationFacilitySpecials(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('削除しました。'));
            } else {
              this.toastr.error(this.translate.instant('削除できませんでした。'));
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('削除できませんでした。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
            this.selection.clear();
          }
        );
      }
    });
  }

  // 戻るボタン
  selectBack(): void {
    this.router.navigate([AppConst.ROUTING_LINK_RESERVATION_FACILITY]);
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'reservation_facility_special_id':
          return compare(a.reservation_facility_special_id, b.reservation_facility_special_id, isAsc);
        case 'type':
          const aAttr = a.reservation_special_date_type ? a.reservation_special_date_type : '';
          const bAttr = b.reservation_special_date_type ? b.reservation_special_date_type : '';
          return compare(aAttr, bAttr, isAsc);
        case 'start_date':
          if (a.reservation_special_start_date && b.reservation_special_start_date) {
            return compare(a.reservation_special_start_date.toMillis(), b.reservation_special_start_date.toMillis(), isAsc);
          } else {
            return 0;
          }
        case 'end_date':
          if (a.reservation_special_end_date && b.reservation_special_end_date) {
            return compare(a.reservation_special_end_date.toMillis(), b.reservation_special_end_date.toMillis(), isAsc);
          } else {
            return 0;
          }
        case 'date':
          return compare(a.updated.toMillis(), b.updated.toMillis(), isAsc);
        default:
          return 0;
      }
    });
  }

  setCustomFilter() {
    // NOTE: AND検索
    this.dataSource.filterPredicate = (data: any, filter: any) => {
      let matchCount = 0;
      let keys = Object.keys(filter);
      for (const key of keys) {
        if (!filter[key]) {
          // フィルタに値がない場合は全抽出
          matchCount++;
        } else {
          if (data[key].trim().toLowerCase().indexOf(filter[key]) !== -1) {
            matchCount++;
          }
        }
      }
      return (keys.length === matchCount);
    };
  }

  addFilterValue(docs: Array<any>): Array<any> {
    for (const doc of docs) {
      let start_date = doc.reservation_special_start_date.toDate().toISOString().split('T')[0];
      let filterValue = '';
      filterValue = start_date.replace(/-/g, '/')
      doc['filterValue'] = filterValue;
      doc['filterValueDateType'] = this.SEARCH_WORD_PREFIX + doc.reservation_special_date_type;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchFilter['filterValue'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

  applyFilterAttr(event: any) {
    this.selection.clear();
    let filterValue = '';
    if (event.value) {
      filterValue = this.SEARCH_WORD_PREFIX + event.value;
    }
    this.searchFilter['filterValueDateType'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

}
